<template>
  <div
    class="main-product component-section critical-component-clear"
    :class="{ [$class('loading')]: !getComponentActive('main-product') || state === 'loading' }"
  >
    <div class="grid">
      <div class="col xs4 l5">
        <product-gallery
          :liquid="liquid"
          :product="product"
          :variant="variant"
        />
      </div>

      <div class="col xs4 l7-11">
        <a
          :href="`/collections/vendors?q=${product.vendor}`"
          style="text-decoration: none;"
        >
          <span
            v-if="product.vendor || liquid.vendor"
            class="main-product__vendor text-heading-m"
            v-text="product.vendor || liquid.vendor"
          />
        </a>

        <h1
          :id="`section-title-${liquid.sectionId}`"
          class="main-product__title text-body-s text-body-m-desktop"
          js-main-product="title"
          v-text="product.title || liquid.title"
        />

        <div
          v-if="uspTags.length > 0"
          class="main-product__usp-tags"
        >
          <span
            v-for="(tag, index) in uspTags"
            :id="`main-product-tag-${index}`"
            :key="`main-product-tag-${index}`"
            class="main-product__tag text-body-label"
            :style="{ backgroundColor: tag.color }"
            v-text="tag.text"
          />
        </div>

        <div class="main-product__prices-container">
          <product-prices
            :available="product?.available"
            block-class="main-product"
            class="limoniapps-discountninja-productprice"
            :compare-at-price="variant?.compareAtPrice?.amount"
            :price="variant?.price?.amount || liquid.price"
            component-location="pdp"
          />
        </div>

        <div class="limoniapps-discountninja-promotioncard-productpage" />

        <div class="main-product__form">
          <product-form
            :liquid="liquid"
            :product="product"
            :variant="variant"
            :current-handle="currentHandle"
            :low-stock-notification="lowStockNotification"
            @update-variant="handleVariantUpdate"
          />
        </div>

        <delivery-messaging />

        <!--<button
          class="main-product__store-check"
          @click="openOverlay({
            component: 'stock-checker',
            ignoreDismissed: true,
            namespace: 'stockChecker',
          })"
        >
          <icon-storecollection />

          <span
            class="text-body-slink text-body-mlink-desktop"
            v-text="$string('product.form.check_store')"
          />
        </button>-->

        <product-information
          :product-info="state === 'loading' ? productInfo : productInfoData"
          :variant="variant"
        />
      </div>
    </div>
  </div>

  <overlay
    namespace="stockChecker"
    :template="false"
    type="modal"
    block-class="stock-checker"
    direction="up"
  >
    <template #body="{isActive}">
      <stock-checker
        v-if="getComponentVisible('stock-checker') && isActive"
        :product="product"
        :variant="variant"
        @close="hideStockChecker"
      />
    </template>
  </overlay>
</template>

<script>

/**
 * Vue: Main product
 * -----------------------------------------------------------------------------
 * Main product page component.
 *
 * @param {String} handle - Product page's original handle.
 * @param {String} liquid.description - Product's Liquid description.
 * @param {Boolean} liquid.hasOnlyDefaultVariant - Has only default variant.
 * @param {String} liquid.price - Product's Liquid price.
 * @param {String} liquid.sectionId - Section's Liquid ID.
 * @param {String} liquid.title - Product's Liquid title.
 * @param {Array} liquid.options - Product's Liquid options.
 * @param {Array} liquid.variants - Product's Liquid variants.
 * @param {Array} uspTags - Object contains product usp tags text and color.
 * @param {Object|Boolean} product - Canvas formatted product object.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 * @param {Object} productInfo - Object containing product data from metafields/pages.
 * @param {Object} lowStockNotification - Object containing low stock notification theme settings.
 *
 */

import { mapActions } from 'vuex'

import DeliveryMessaging from '~async/delivery-messaging/delivery-messaging'
import ProductForm from '~async/product-form/product-form'
import ProductInformation from '~async/product-information/product-information'
import ProductGallery from '~async/product-gallery/product-gallery'
import ProductPrices from '~global/product-prices/product-prices'
import StockChecker from '~async/stock-checker/stock-checker'

import IconStorecollection from '~icons/misc/store-collection.svg'

import product from '~/graphql/queries/product.gql'
import { formatProduct } from '~/helpers/format'

export default {
  name: 'MainProduct',

  components: {
    DeliveryMessaging,
    ProductForm,
    ProductInformation,
    ProductPrices,
    IconStorecollection,
    ProductGallery,
    ProductPrices,
    StockChecker,
  },

  props: {
    handle: {
      type: String,
      default: cnvs.page.handle,
    },
    liquid: {
      type: Object,
      default: () => ({
        description: '',
        hasOnlyDefaultVariant: false,
        price: 0,
        sectionId: '',
        title: '',
        previousSlideLabel: '',
        nextSlideLabel: '',
        openFullscreenLabel: '',
        closeFullscreenLabel: '',
        options: [],
        variants: [],
      }),
    },
    uspTags: {
      type: Array,
      default: () => ([]),
    },
    product: {
      type: [Object, Boolean],
      default: false,
    },
    variant: {
      type: [Object, Boolean],
      default: false,
    },
    productInfo: {
      type: Object,
      default: () => ({}),
    },
    lowStockNotification: {
      type: Object,
      default: () => ({}),
    },
  },

  emits: ['update-request-object'],

  data() {
    return {
      currentHandle: cnvs.page.handle,
      state: 'loading',
    }
  },

  computed: {

    /**
     * Compute product info data.
     * @returns {Object}
     */
    productInfoData() {
      if (!this.product) {
        return this.productInfo
      }

      return {
        origin: this.product.origin,
        sku: this.variant.sku,
        directions: this.product.directions,
        keyFeatures: this.product.keyFeatures,
        video: this.productInfo.video,
        refundsContent: this.productInfo.refundsContent,
      }
    },
  },

  watch: {

    /**
     * Watch currentHandle for update to load new product.
     */
    currentHandle() {
      this.queryProduct(this.currentHandle)
    },

    // Watch product for update to push the detail datalayer event.
    product() {
      this.productDataLayer(this.product)
    },

  },

  mounted() {
    this.queryProduct()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
      closeOverlay: 'overlays/close',
    }),

    /**
     * Loads product from GraphQL.
     * @param {String} [handle] - Product handle to load.
     */
    async queryProduct(handle = this.handle) {
      if (handle === false) {
        return
      }

      if (this.product.handle === handle) {
        this.state = 'ready'
        return
      }

      try {
        const response = await cnvs.Query({
          locale: cnvs.store.language,
          query: product,
          variables: {
            country: cnvs.store.country,
            handle,
          },
        })

        if (cnvs.settings.disableComponentReadyState) {
          return
        }

        this.$emit('update-request-object', {
          response: formatProduct(response),
          type: 'product',
        })

        this.state = 'ready'

      } catch (error) {
        reportError('Failed to load product', error)
      }
    },

    /**
     * Handle variant update from product form.
     * @param {Object} variant - Selected variant.
     */
    handleVariantUpdate(variant) {
      this.$emit('update-request-object', {
        response: variant,
        type: 'variant',
      })

      document.dispatchEvent(new CustomEvent('limoniapps:discountninja:variant:changed', { detail: { variant: variant.id } }))

      const url = variant?.id
        ? `${location.origin}${location.pathname}?variant=${variant.id}`
        : `${location.origin}${location.pathname}`

      history.replaceState({
        html: '',
      }, '', url)
    },

    /**
     * Events for when the close Stock Checker button is clicked.
     */
    hideStockChecker() {
      this.closeOverlay('stockChecker')
    },

    /**
     * Send the product info to the data layer.
     */
    productDataLayer(product) {
      cnvs.EventBus.emit('Datalayer:productDetails', product)

      if (product.available) {
        return
      }

      cnvs.EventBus.emit('Datalayer:productOutOfStock', product)
    },

  },
}
</script>

<style lang="scss">
@import './main-product';
</style>
